import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, finalize, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  ReportsApiService,
  type AgentActivityDetailPayload,
  type ReportDetailSummary,
  type ReportSessionRow,
} from 'src/app/core/services/reports-api.service';
import { trimmedOrUndefined } from 'src/app/shared/utils/string.util';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { formatReportDuration } from '../report-duration.util';
import { isReportAgentRef } from '../reports-route.util';
import { isSuperAdminSimulatePdSession } from 'src/app/shared/utils/pd-session-list.util';

const PAGE_SIZE = 15;

@Component({
  selector: 'app-report-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, SharedModule, MaterialModule, IconModule],
  templateUrl: './report-detail.component.html',
  styleUrl: './report-detail.component.css',
})
export class ReportDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly reportsApi = inject(ReportsApiService);
  private readonly destroy$ = new Subject<void>();

  agentRef = '';
  loading = true;
  loadError = '';
  summary: ReportDetailSummary | null = null;
  sessions: AgentActivityDetailPayload['sessions'] | null = null;

  page = 1;
  readonly pageSize = PAGE_SIZE;
  dateFrom = '';
  dateTo = '';

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const ref = (params.get('agentRef') ?? '').trim();
      if (!isReportAgentRef(ref)) {
        this.loadError = 'Invalid report link.';
        this.loading = false;
        return;
      }
      this.agentRef = ref;
      this.syncFiltersFromQuery();
      this.page = 1;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncFiltersFromQuery(): void {
    const q = this.route.snapshot.queryParamMap;
    this.dateFrom = q.get('date_from') ?? '';
    this.dateTo = q.get('date_to') ?? '';
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    this.reportsApi
      .getAgentActivityDetail(this.agentRef, {
        date_from: this.dateFrom || undefined,
        date_to: this.dateTo || undefined,
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(assertSuccessEnvelope(), finalize(() => (this.loading = false)), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          if (!env.data) {
            this.loadError = 'Report not found.';
            this.summary = null;
            this.sessions = null;
            return;
          }
          this.summary = env.data.summary;
          this.sessions = env.data.sessions;
          this.page = env.data.sessions.page ?? this.page;
        },
        error: (err) => {
          this.loadError = resolveApiErrorMessage(err) || 'Could not load report details.';
          this.summary = null;
          this.sessions = null;
        },
      });
  }

  get sessionItems(): ReportSessionRow[] {
    return this.sessions?.items ?? [];
  }

  get sessionTotal(): number {
    return this.sessions?.total ?? 0;
  }

  get totalPages(): number {
    return Math.ceil(this.sessionTotal / this.pageSize) || 1;
  }

  get pageStart(): number {
    return this.sessionTotal === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.page * this.pageSize, this.sessionTotal);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, this.page - 2);
    const end = Math.min(this.totalPages, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.load();
  }

  backToReports(): string[] {
    return ['/reports'];
  }

  backQueryParams(): Record<string, string> {
    const q: Record<string, string> = {};
    if (this.dateFrom) q['date_from'] = this.dateFrom;
    if (this.dateTo) q['date_to'] = this.dateTo;
    return q;
  }

  dateDisplay(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  timeDisplay(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  eventLabel(s: ReportSessionRow): string {
    return trimmedOrUndefined(s.loan_type) ?? s.status ?? 'Session';
  }

  statusLabel(result: string | null): 'PASS' | 'REVIEW' | 'FAIL' | 'PENDING' {
    if (result === 'PASS') return 'PASS';
    if (result === 'REVIEW') return 'REVIEW';
    if (result === 'FAIL') return 'FAIL';
    return 'PENDING';
  }

  statusClass(result: string | null): string {
    const r = this.statusLabel(result);
    if (r === 'PASS') return 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50';
    if (r === 'REVIEW') return 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-950/50';
    if (r === 'FAIL') return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950/50';
    return 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-white/5';
  }

  scoreDisplay(score: number | null): string {
    if (score == null) return '—';
    return `${Math.round(Number(score))}`;
  }

  durationDisplay(seconds?: number | null, hours?: number | null): string {
    return formatReportDuration(seconds, hours);
  }

  readonly canOpenSessionInAuditTrail = isSuperAdminSimulatePdSession;

  viewSession(s: ReportSessionRow, event?: Event): void {
    event?.stopPropagation();
    if (!isSuperAdminSimulatePdSession(s)) return;
    void this.router.navigate(['/audit-trail'], {
      queryParams: {
        session: String(s.id),
        return: this.router.url,
      },
    });
  }

  agentDetailLink(): string[] | null {
    const uuid = this.summary?.agent_uuid?.trim();
    if (!uuid) return null;
    return ['/manage-agent', uuid];
  }

  auditTrailQueryParams(): Record<string, string> {
    const q: Record<string, string> = {};
    if (this.summary?.agent_db_id) q['agent_id'] = String(this.summary.agent_db_id);
    if (this.dateFrom) q['date_from'] = this.dateFrom;
    if (this.dateTo) q['date_to'] = this.dateTo;
    q['return'] = this.router.url;
    return q;
  }
}
